/**
 * §8's `contentRegion`, and the reason it needs an allowlist rather than a regex.
 *
 * `GET /api/v1/trending?region=XX` answers **500 with a leaked Crystal `KeyError`** for any region
 * YouTube's trending browse endpoint does not serve:
 *
 * ```
 * 500 {"error":"Missing hash key: \"content\"","errorBacktrace":"… (KeyError)\n from ???"}
 * ```
 *
 * and an ISO-3166 check is **not** enough to avoid it. Measured on 2026-07-29: `AQ`, `VA`, `KP`,
 * `CU`, `SS`, `EH`, `AX`, `GG`, `CN`, `FJ`, `MM`, `BT` and `MC` are all valid ISO-3166-1 alpha-2
 * codes and every one of them 500s, while all 12 codes tried from the list below answered 200. An
 * **empty** `region=` 500s too — the same shape as the empty `Authorization` trap (CLAUDE.md): the
 * natural way to say "no preference" is the one value that breaks it, so an absent region must
 * omit the parameter rather than send it blank.
 *
 * Because that 500 carries `errorBacktrace`, it classifies as `upstream-broken` — never retried (§3)
 * — so an unvalidated region costs the instance nothing beyond the one request.
 * It just makes Trending permanently empty with an error that reads like the instance is broken,
 * which is precisely the failure mode client-side validation exists to prevent.
 *
 * The list is the instance's **own**: Invidious renders a region `<select>` on `/preferences`, and
 * these 107 codes are its options, read on 2026-07-29 from `invidious.example.com`. That makes it
 * the set the software itself believes YouTube serves, rather than a set we assembled by guessing.
 */
export const CONTENT_REGIONS: readonly string[] = [
  'AE', 'AR', 'AT', 'AU', 'AZ', 'BA', 'BD', 'BE', 'BG', 'BH', 'BO', 'BR', 'BY', 'CA', 'CH', 'CL',
  'CO', 'CR', 'CY', 'CZ', 'DE', 'DK', 'DO', 'DZ', 'EC', 'EE', 'EG', 'ES', 'FI', 'FR', 'GB', 'GE',
  'GH', 'GR', 'GT', 'HK', 'HN', 'HR', 'HU', 'ID', 'IE', 'IL', 'IN', 'IQ', 'IS', 'IT', 'JM', 'JO',
  'JP', 'KE', 'KR', 'KW', 'KZ', 'LB', 'LI', 'LK', 'LT', 'LU', 'LV', 'LY', 'MA', 'ME', 'MK', 'MT',
  'MX', 'MY', 'NG', 'NI', 'NL', 'NO', 'NP', 'NZ', 'OM', 'PA', 'PE', 'PG', 'PH', 'PK', 'PL', 'PR',
  'PT', 'PY', 'QA', 'RO', 'RS', 'RU', 'SA', 'SE', 'SG', 'SI', 'SK', 'SN', 'SV', 'TH', 'TN', 'TR',
  'TW', 'TZ', 'UA', 'UG', 'US', 'UY', 'VE', 'VN', 'YE', 'ZA', 'ZW',
];

/**
 * A stored or user-typed region → the value that may go on the wire, or `null` for "omit it".
 *
 * `null` rather than a default of `'US'`: omitting the parameter already means the instance's own
 * default (measured — `?region=US` and a bare call return byte-identical lists), and substituting
 * a region the user never chose is the kind of quiet default that later reads as a bug.
 *
 * Case is normalised because the endpoint accepts `de` as readily as `DE`, so the two must not
 * become different settings.
 */
export function normalizeContentRegion(input: string | null | undefined): string | null {
  if (input === null || input === undefined) return null;
  const code = input.trim().toUpperCase();
  if (code === '') return null;
  return CONTENT_REGIONS.includes(code) ? code : null;
}

/** Whether a code is one the trending endpoint will serve — the same question, as a predicate. */
export function isContentRegion(input: string): boolean {
  return normalizeContentRegion(input) !== null;
}
